/**
 * 微信公众号授权登录工具类
 * 处理微信OAuth2.0授权登录流程
 */
import { wxLoginApi } from '@/api/pay';

class WechatAuth {
  constructor() {
    // 微信公众号appid（从环境变量读取）
    this.appid = process.env.VUE_APP_WECHAT_APPID || 'wxc3fa9dcc3519bc59';

    // 构建回调URL
    // 微信后台只需配置域名（如：vip.miaojiya.com）
    // 但 redirect_uri 参数需要完整URL（如：https://vip.miaojiya.com/）
    const protocol = window.location.protocol; // https: 或 http:
    const hostname = window.location.hostname; // vip.miaojiya.com
    const port = window.location.port ? `:${window.location.port}` : ''; // 生产环境通常为空
    const callbackPath = '/'; // 回调到首页

    const fullCallbackUrl = `${protocol}//${hostname}${port}${callbackPath}`;
    this.redirectUri = encodeURIComponent(fullCallbackUrl);

    // 应用授权作用域 snsapi_base静默授权 snsapi_userinfo需用户手动同意
    this.scope = 'snsapi_userinfo';
    // 用于保持请求和回调状态的参数
    this.state = 'wechat_login';
  }

  /**
   * 验证redirect_uri配置是否正确
   * @returns {Object} 验证结果
   */
  validateRedirectUri() {
    const decoded = decodeURIComponent(this.redirectUri);
    const urlObj = new URL(decoded);

    return {
      encoded: this.redirectUri,
      decoded: decoded,
      protocol: urlObj.protocol,
      hostname: urlObj.hostname,
      pathname: urlObj.pathname,
      fullUrl: urlObj.href,
      expectedDomain: 'vip.miaojiya.com',
      isDomainCorrect: urlObj.hostname === 'vip.miaojiya.com',
      isPathCorrect: urlObj.pathname === '/',
    };
  }

  /**
   * 跳转到微信授权页面
   */
  redirectToWechatAuth() {
    const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${this.appid}&redirect_uri=${this.redirectUri}&response_type=code&scope=${this.scope}&state=${this.state}#wechat_redirect`;

    // 详细的调试日志
    console.log('【微信授权调试信息】');
    console.log('  AppID:', this.appid);
    console.log('  Redirect URI (encoded):', this.redirectUri);
    console.log('  Redirect URI (decoded):', decodeURIComponent(this.redirectUri));
    console.log('  Scope:', this.scope);
    console.log('  State:', this.state);
    console.log('  当前页面URL:', window.location.href);
    console.log('  当前协议:', window.location.protocol);
    console.log('  当前主机名:', window.location.hostname);
    console.log('  当前端口:', window.location.port || '(默认端口)');
    console.log('【完整授权URL】', authUrl);

    // 添加延迟确保日志输出
    setTimeout(() => {
      console.log('【执行微信授权重定向】');
      window.location.href = authUrl;
    }, 100);
  }

  /**
   * 从URL中获取微信授权code
   * @returns {string|null} 授权code或null
   */
  getWechatCode() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('code');
  }

  /**
   * 检查是否是微信浏览器
   * @returns {boolean} 是否为微信浏览器
   */
  isWechatBrowser() {
    const ua = navigator.userAgent.toLowerCase();
    // 增强判断逻辑，支持微信开发者工具
    return ua.includes('micromessenger') || ua.includes('wechatdevtools');
  }

  /**
   * 从服务器获取用户信息（需后端接口支持）
   * @param {string} code 微信授权code
   * @returns {Promise<Object>} 用户信息
   */
  async fetchUserInfoFromServer(code) {
    try {
      const response = await wxLoginApi({ jsonData: JSON.stringify({ code }) });
      console.log('【获取微信用户信息成功】', response);
      if (response.code !== 10000) {
        throw new Error(`API error! code: ${response.code}, message: ${response.message}`);
      }
      return response.data;
    } catch (error) {
      console.error('获取微信用户信息失败:', error);
      throw error;
    }
  }

  /**
   * 设置用户信息
   * @param {Object} userInfo 用户信息对象
   */
  setUserInfo(userInfo) {
    localStorage.setItem('wechat_user_info', JSON.stringify(userInfo));
    // 如果返回了token，同时保存token
    if (userInfo.token) {
      this.setToken(userInfo.token);
    }
  }

  /**
   * 获取用户信息
   * @returns {Object|null} 用户信息或null
   */
  getUserInfo() {
    const userInfo = localStorage.getItem('wechat_user_info');
    return userInfo ? JSON.parse(userInfo) : null;
  }

  /**
   * 设置token
   * @param {string} token 令牌
   */
  setToken(token) {
    localStorage.setItem('token', token);
  }

  /**
   * 获取token
   * @returns {string|null} 令牌或null
   */
  getToken() {
    return localStorage.getItem('token');
  }

  /**
   * 清除登录信息
   */
  clearAuth() {
    localStorage.removeItem('wechat_user_info');
    localStorage.removeItem('token');
  }

  /**
   * 检查是否已登录
   * @returns {boolean} 是否已登录
   */
  isLoggedIn() {
    const userInfo = this.getUserInfo();
    return !!(userInfo && userInfo.id);
  }
}

// 创建单例实例
const wechatAuth = new WechatAuth();

export default wechatAuth;
